// src/pages/InvestmentManagementPage.tsx

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiDollarSign, FiBarChart2, FiList, FiAlertTriangle, FiLock } from 'react-icons/fi';
import styles from './InvestmentManagementPage.module.css';
import type { Investment } from '../types/Project';
import { InvestmentsService, InvestmentGate } from '../services/investments.service';
import { useNotification } from '../context/NotificationContext';

const MXN_TO_USD_RATE = 0.058;

const statusTranslationMap: Record<string, string> = {
  Activa: 'status.active',
  Pendiente: 'status.pending',
  Aprobado: 'status.approved',
  Rechazado: 'status.rejected',
  Finalizada: 'status.completed',
  Cerrada: 'status.closed',
  Cerrado: 'status.closed',
  Inactiva: 'status.inactive',
  Inactivo: 'status.inactive'
};

const statusClassMap: Record<string, string> = {
  Activa: styles.statusActiva,
  Pendiente: styles.statusPendiente,
  Aprobado: styles.statusAprobado,
  Rechazado: styles.statusRechazado,
  Finalizada: styles.statusFinalizada,
  Cerrada: styles.statusFinalizada,
  Cerrado: styles.statusFinalizada,
  Inactiva: styles.statusFinalizada,
  Inactivo: styles.statusFinalizada
};

interface StatCardProps {
  titleKey: string;
  value: string;
  icon: React.ElementType;
  color: string;
}

const StatCard = ({ titleKey, value, icon: Icon, color }: StatCardProps) => {
  const { t } = useTranslation();

  return (
    <div className={styles.statCard} style={{ borderLeftColor: color }}>
      <div className={styles.cardContent}>
        <p className={styles.cardTitle}>{t(titleKey)}</p>
        <h2 className={styles.cardValue}>{value}</h2>
      </div>
      <div className={styles.cardIconWrap} style={{ color }}>
        <Icon className={styles.cardIcon} />
      </div>
    </div>
  );
};

interface InvestmentRowProps {
  investment: Investment;
  t: (key: string) => string;
  formatCurrency: (amountMXN: number) => string;
  onViewDetails: (investment: Investment) => void;
}

const InvestmentRow = ({ investment, t, formatCurrency, onViewDetails }: InvestmentRowProps) => {
  const statusValue = (investment as any)?.status as string;
  const statusClass = statusClassMap[statusValue] ?? styles.statusDefault;
  const statusKey = statusTranslationMap[statusValue] ?? 'status.unknown';

  return (
    <tr className={styles.tableRow}>
      <td className={styles.projectCell}>
        <Link to={`/proyectos/${(investment as any).projectId}`} className={styles.projectLink}>
          {(investment as any).projectTitle}
        </Link>
      </td>
      <td>{(investment as any).date}</td>
      <td>{formatCurrency(Number((investment as any).investedAmount || 0))}</td>
      <td>{formatCurrency(Number((investment as any).expectedReturn || 0))}</td>
      <td>
        <span className={statusClass}>{t(statusKey)}</span>
      </td>
      <td className={styles.actionsCell}>
        <button
          type="button"
          className={styles.viewDetailsButton}
          onClick={() => onViewDetails(investment)}
        >
          {t('common.view_details')}
        </button>
      </td>
    </tr>
  );
};

const InvestmentManagementPage = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { addNotification } = useNotification();

  const [investments, setInvestments] = useState<Investment[]>([]);
  const [gate, setGate] = useState<InvestmentGate | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedInvestment, setSelectedInvestment] = useState<Investment | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [list, g] = await Promise.all([InvestmentsService.getMyInvestments(), InvestmentsService.getGate()]);
      setInvestments(Array.isArray(list) ? list : []);
      setGate(g || null);
    } catch {
      setInvestments([]);
      setGate(null);
    } finally {
      setLoading(false);
    }
  };

  // Manejar notificaciones de pago desde query params
  useEffect(() => {
    const status = searchParams.get('status');
    const invoice = searchParams.get('invoice');
    const order = searchParams.get('order');
    
    if (status === 'success' && invoice) {
      addNotification(
        `Pago exitoso. Invoice: ${invoice}${order ? `, Order: ${order}` : ''}`,
        'success'
      );
      // Limpiar params
      searchParams.delete('status');
      searchParams.delete('invoice');
      searchParams.delete('order');
      setSearchParams(searchParams, { replace: true });
    } else if (status === 'error') {
      const errorMsg = searchParams.get('message') || 'Error al procesar el pago';
      addNotification(errorMsg, 'error');
      // Limpiar params
      searchParams.delete('status');
      searchParams.delete('message');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, addNotification]);

  useEffect(() => {
    load();
  }, []);

  const isSpanish = i18n.language.startsWith('es');
  const locale = isSpanish ? 'es-MX' : 'en-US';
  const currency = isSpanish ? 'MXN' : 'USD';

  const formatCurrency = (amountMXN: number): string => {
    const finalAmount = isSpanish ? amountMXN : amountMXN * MXN_TO_USD_RATE;
    return Number(finalAmount || 0).toLocaleString(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0
    });
  };

  const formatDateTime = (value?: string) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString(locale);
  };

  const formatPercent = (value: number) => {
    if (!Number.isFinite(value)) return '—';
    return `${value.toFixed(2)}%`;
  };

  const closeModal = () => setSelectedInvestment(null);

  const totalInvestedMXN = useMemo(
    () => investments.reduce((sum, inv) => sum + Number((inv as any).investedAmount || 0), 0),
    [investments]
  );
  const totalReturnMXN = useMemo(
    () => investments.reduce((sum, inv) => sum + Number((inv as any).expectedReturn || 0), 0),
    [investments]
  );

  const activeInvestments = useMemo(() => {
    return investments.filter(inv => {
      const s = String((inv as any).status || '');
      return s === 'Activa' || s === 'Aprobado' || s === 'active' || s === 'approved';
    }).length;
  }, [investments]);

  if (loading) return <div>Cargando...</div>;

  return (
    <div className={styles.pageContainer}>
      <header className="module-header">
        <h1 className="module-title">{t('common.investments')}</h1>
        <p className="module-subtitle">{t('investments.page_subtitle')}</p>
      </header>

      {gate && !gate.canInvest ? (
        <div className={styles.gateCard} role="status" aria-live="polite">
          <div className={styles.gateIcon}>
            <FiLock />
          </div>
          <div className={styles.gateBody}>
            <div className={styles.gateTitle}>{t('investments.blocked_title') || 'Acción restringida'}</div>
            <div className={styles.gateText}>
              {gate.reasons && gate.reasons.length > 0
                ? gate.reasons.join(' • ')
                : t('investments.blocked_kyc_pld') || 'Completa KYC/PLD para operar.'}
            </div>
            <div className={styles.gateCta}>
              <Link to="/perfil" className={styles.gateLink}>
                {t('investments.go_to_profile') || 'Ir a Perfil (KYC/PLD)'}
              </Link>
            </div>
          </div>
        </div>
      ) : null}

      <div className={styles.statsGrid}>
        <StatCard
          titleKey="investments.total_invested"
          value={formatCurrency(totalInvestedMXN)}
          icon={FiDollarSign}
          color="var(--color-primario)"
        />
        <StatCard
          titleKey="investments.active_count"
          value={activeInvestments.toString()}
          icon={FiList}
          color="#16a34a"
        />
        <StatCard
          titleKey="investments.expected_return"
          value={formatCurrency(totalReturnMXN)}
          icon={FiBarChart2}
          color="#f59e0b"
        />
      </div>

      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>{t('investments.investment_history')}</h2>
      </div>

      {investments.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            <FiAlertTriangle />
          </div>
          <div className={styles.emptyTitle}>{t('investments.no_investments')}</div>
          <div className={styles.emptyCta}>
            <Link to="/proyectos" className={styles.emptyLink}>
              {t('investments.explore_more')}
            </Link>
          </div>
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.investmentTable}>
            <thead>
              <tr>
                <th>{t('investments.table_project')}</th>
                <th>{t('investments.table_date')}</th>
                <th>{t('investments.table_amount')}</th>
                <th>{t('investments.table_expected_return')}</th>
                <th>{t('investments.table_status')}</th>
                <th className={styles.thActions}>{t('investments.table_actions')}</th>
              </tr>
            </thead>
            <tbody>
              {investments.map(inv => (
                <InvestmentRow
                  key={String((inv as any).id)}
                  investment={inv}
                  t={t}
                  formatCurrency={formatCurrency}
                  onViewDetails={setSelectedInvestment}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className={styles.ctaLink}>
        <Link to="/proyectos">{t('investments.explore_more')}</Link>
      </div>

      {selectedInvestment ? (
        <div className={styles.modalOverlay} role="dialog" aria-modal="true" onClick={closeModal}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div>
                <h3 className={styles.modalTitle}>
                  {t('investments.detail_title') || 'Detalle de inversión'}
                </h3>
                <p className={styles.modalSubtitle}>
                  {(selectedInvestment as any).projectTitle}
                </p>
              </div>
              <button type="button" className={styles.modalClose} onClick={closeModal} aria-label="Cerrar">
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.detailGrid}>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>{t('investments.detail_project') || 'Proyecto'}</span>
                  <span className={styles.detailValue}>{(selectedInvestment as any).projectTitle}</span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>{t('investments.detail_status') || 'Estado'}</span>
                  <span className={styles.detailValue}>
                    {t(statusTranslationMap[(selectedInvestment as any).status] || 'status.unknown')}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>{t('investments.detail_amount') || 'Monto invertido'}</span>
                  <span className={styles.detailValue}>
                    {formatCurrency(Number((selectedInvestment as any).investedAmount || 0))}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>{t('investments.detail_return') || 'Rendimiento'}</span>
                  <span className={styles.detailValue}>
                    {formatCurrency(Number((selectedInvestment as any).expectedReturn || 0))}
                  </span>
                  {String((selectedInvestment as any).status || '') !== 'Finalizada' ? (
                    <span className={styles.detailHint}>
                      {t('investments.return_pending') || 'Se calcula al finalizar el proyecto.'}
                    </span>
                  ) : null}
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>{t('investments.detail_date') || 'Fecha de inversión'}</span>
                  <span className={styles.detailValue}>
                    {formatDateTime((selectedInvestment as any).date)}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>
                    {t('investments.detail_gain_percent') || 'Porcentaje de ganancia'}
                  </span>
                  <span className={styles.detailValue}>
                    {formatPercent(
                      Number((selectedInvestment as any).investedAmount || 0) > 0
                        ? (Number((selectedInvestment as any).expectedReturn || 0) /
                            Number((selectedInvestment as any).investedAmount || 0)) *
                            100
                        : 0
                    )}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>
                    {t('investments.detail_total_return') || 'Rendimiento final'}
                  </span>
                  <span className={styles.detailValue}>
                    {formatCurrency(
                      Number((selectedInvestment as any).investedAmount || 0) +
                        Number((selectedInvestment as any).expectedReturn || 0)
                    )}
                  </span>
                </div>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.modalPrimary} onClick={closeModal}>
                {t('common.close') || 'Cerrar'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default InvestmentManagementPage;
